import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { cloneDeep, includes, isArray } from 'lodash';

const INDEX_COLUMN_CONFIG: ITableColumn = {
  columnName: 'Sl no',
  isSortable: true,
  type: 'indexed'
}

const ADD_COLUMN_CONFIG: ITableColumn = {
  columnName: 'Add Row',
  type: 'add-column',
}

interface ITableConfig {
  tableDataSource: MatTableDataSource<any>,
  tableColumns: ITableColumn[],
  paginationConfig?: IPaginationConfig,
  displayedColumns?: string[],
  showAddColumn?: boolean;
  showLoader?: boolean;
  shouldChangeBackgroundOnHover?: boolean;
  isDragAndDropEnabled?: boolean;
  selectedRow?: any
  headerRowStyles?: any
}
export interface IEmptyStateConfig {
  heading?: string;
  content?: string;
}

interface IAdditionalColumn extends ITableColumn {
  key: string;
  checked: boolean;
}

type columnType = 'text' | 'user' | 'date' | 'form' | 'htmlDivElement' | 'indexed' | 'add-column' | 'editable-content' | 'any'

interface IPaginationConfig {
  pageSizeOptions?: number[],
  limit?: number,
  page?: number,
  shouldShowFirstLastButtons?: boolean,
  emitPageEvent?: (page) => void;
}

interface IFormFieldConfig {
  type: 'dropdown' | 'checkbox';
  data: any[];
  value?: string;
  dropdownOptions?: { label: string, id: string, disabled: boolean }[]
  checkboxConfig?: { checked: boolean, disabled: boolean };
  displayKey?: string;
  selectedValue?: any;
  fieldDisabled?: boolean;
  onOptionSelect?: (tableColumn, event, element) => void;
  onCheckboxClick?: (tableColumn, event, element) => void;
}

export interface IImageConfig {
  imagePosition: 'before' | 'after',
  imageElement: ElementRef,
}

export interface ITableColumn {
  columnName: string,
  dataKey?: string,
  isSortable?: boolean,
  type?: columnType,
  isSticky?: boolean,
  imageConfig?: IImageConfig,
  getTooltipText?: (element) => string;
  isArray?: boolean;
  isNested?: boolean;
  nestedPath?: string;
  formfieldConfig?: IFormFieldConfig;
  onClick?: (element) => void;
  contentToShowOnHover?: ElementRef;
  customClass?: string;
  isReadOnly?: boolean;
  isDraggable?: boolean;
  onEdit?: (editedContent, element) => void;
  divElement?: any,
  showHeader?: boolean
}

@Component({
  selector: 'kt-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss']
})
export class TableComponent implements OnChanges, AfterViewInit {

  @Input() tableConfig!: ITableConfig;
  @Input() isTableLoading: boolean = false;
  @Input() additionalColumns !: IAdditionalColumn[];
  @Input() emptyStateConfig: IEmptyStateConfig | null = null;

  @Output() onColumnSort = new EventEmitter();
  @Output() onColumnDrag = new EventEmitter();
  @Output() onColumnSelect = new EventEmitter();
  @Output() handleRowHover = new EventEmitter();
  @Output() onSaveColumnDrop = new EventEmitter();

  @ViewChild('paginator') paginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort: MatSort;
  @ViewChild(MatTable) public table!: MatTable<any>;
  @ViewChild('addRowElement') addRowElement: ElementRef<any>;
  @ViewChild('menuTrigger') menuTrigger: ElementRef;
  @ViewChild('addColumnButton') addColumnButton: ElementRef;
  @ViewChild('input') input: ElementRef;

  tableColumns: ITableColumn[];
  displayedColumns: string[] = [];
  datasource = new MatTableDataSource();
  intialAdditionalColumns: IAdditionalColumn[];

  pageSizeOptions = [10, 25, 50];
  limit = 25;
  page: number = 0;
  shouldShowFirstLastButtons = true;

  isMenuOpen = false;
  showLoader = false;
  isColumnDragged = false;
  showAddColumn: boolean = false;
  hoveredRowIndex: number;
  indexColumn = INDEX_COLUMN_CONFIG;
  addColumn = ADD_COLUMN_CONFIG;
  currentSelectedRow: any = null;
  inputValue: string = '';
  shouldChangeBackgroundOnHover: boolean;
  tooltipText: string;
  constructor(private readonly cdr: ChangeDetectorRef, private readonly element: ElementRef) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.tableConfig?.currentValue) {
      this.tableConfig = { ...changes.tableConfig.currentValue };
      this.tableConfig.tableDataSource.sort = this.sort;
      this.tableConfig.tableDataSource.data = changes.tableConfig.currentValue.tableDataSource.data;
      this.currentSelectedRow = this.tableConfig.selectedRow;
      this.handleRowClick(this.currentSelectedRow);
      this.tableColumns = changes.tableConfig.currentValue.tableColumns;
      this.tableColumns?.forEach((column) => {
        if (!column.hasOwnProperty('showHeader'))
          column.showHeader = true
      })
      this.displayedColumns = this.tableConfig?.tableColumns?.map((column) => {
        return column.columnName
      })
      this.shouldChangeBackgroundOnHover = changes.tableConfig.currentValue?.shouldChangeBackgroundOnHover;
      if (this.tableConfig?.showAddColumn) {
        this.displayedColumns.push('addRow')
      }
    }

    if (changes?.additionalColumns) {
      this.intialAdditionalColumns = cloneDeep(changes?.additionalColumns?.currentValue)
      this.additionalColumns = changes?.additionalColumns?.currentValue
    }

    if (changes.showLoader) {
      this.showLoader = changes.showLoader.currentValue
      this.closeMenu()
    }
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
    if (this.tableConfig?.paginationConfig) {
      this.pageSizeOptions = this.tableConfig?.paginationConfig?.pageSizeOptions;
      this.page = this.tableConfig?.paginationConfig?.page;
      this.limit = this.tableConfig?.paginationConfig?.limit;
      this.shouldShowFirstLastButtons = this.tableConfig?.paginationConfig?.shouldShowFirstLastButtons;
    } else {
      this.tableConfig.tableDataSource.paginator = this.paginator;
    }
  }

  ngAfterViewChecked() {
    this.table?.updateStickyColumnStyles();
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: Event) {
    const targetElement = event.target;
    event.stopPropagation();
    if (!this.addColumnButton?.nativeElement?.contains(event.target)) {
      if (this.addColumnButton?.nativeElement?.contains(event.target) || this.menuTrigger?.nativeElement?.contains(event.target)) {
        this.isMenuOpen = true;
      }
      else if ((!this.addColumnButton?.nativeElement?.contains(event.target) && !this.menuTrigger?.nativeElement?.contains(event.target) && this.intialAdditionalColumns?.length)) {
        this.additionalColumns = [...this.intialAdditionalColumns]
        this.isMenuOpen = false;
      }
    }
  }

  getIsReadOnly(tableColumn, row) {
    row = row + (this.page * this.limit);
    const col = this.tableColumns.find((column) => column.columnName == tableColumn.columnName);
    let isReadOnly;
    if (row === this.currentSelectedRow) {
      isReadOnly = col.isReadOnly;
    }
    else {
      isReadOnly = true;
    }
    return isReadOnly;
  }

  handleEdit(event, element, tableColumn) {
    if (tableColumn?.onEdit)
      tableColumn?.onEdit(this.inputValue, element);
    this.currentSelectedRow = null;
    this.inputValue = ''
  }

  handleRowClick(row) {
    this.currentSelectedRow = row;
  }

  handleChange(event) {
    this.inputValue += event.target.value
  }

  onRowHover(index) {
    this.hoveredRowIndex = (this.page * this.limit) + index;
    this.handleRowHover.emit(this.hoveredRowIndex);
  }

  onRowLeave() {
    this.hoveredRowIndex = null;
  }

  getRemainingUserCount(element: any, dataKey, tableColumn: any, type: 'count' | 'tooltipText') {
    const users = this.getNestedValue(element, tableColumn?.dataKey, tableColumn);
    if (type === 'count')
      return users.length > 4 ? users.length - 4 : 0;
    return users?.slice(4).join(', ');
  }

  getTooltipText(element: any, dataKey, tableColumn: any) {
    const users = this.getNestedValue(element, tableColumn?.dataKey, tableColumn);
    return users?.slice(2).join(', ');
  }

  //This method is written to retrive nested values of datasource
  getNestedValue(element: any, path: string, column: ITableColumn): any {
    if (!path) return element;
    if (column?.isNested && column?.isArray && column?.nestedPath) {
      let fields = [];
      const paths = path.split('.');
      let selectedElement;
      for (let p of paths) {
        if (element[p]) {
          selectedElement = element[p]
          break;
        }
      }
      if (isArray(selectedElement)) {
        selectedElement.map((element) => {
          fields.push(element[column?.nestedPath]);
        })
      }
      return fields;
    }
    const value = path.split('.').reduce((acc, part) => acc && acc[part], element);
    if (column?.type == 'date') {
      return this.getDateString(value);
    }
    return value;
  }

  getHoveredRowStyles(index) {
    return this.tableConfig.shouldChangeBackgroundOnHover && index === this.hoveredRowIndex;
  }

  handleClickOnInputElement(tableColumn, i, element) {
    if (this.getIsReadOnly(tableColumn, i)) {
      tableColumn.onClick(element);
    }
  }

  getDateString(date) {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  onDropdownOptionSelect(tableColumn, event, element) {
    tableColumn?.formfieldConfig.onOptionSelect(tableColumn, event, element);
  }

  handleCheckboxClick(tableColumn, event, element) {
    tableColumn?.formfieldConfig.onCheckboxClick(tableColumn, event, element);
  }

  sortData(sort: Sort) {
    this.onColumnSort.emit(sort)
  }

  openColumnMenu() {
    this.isMenuOpen = true;
  }

  closeMenu() {
    if (!this.showLoader && this.isMenuOpen) {
      this.isMenuOpen = false;
    }
    if (!this.showLoader && this.isColumnDragged)
      this.isColumnDragged = false;

    this.cdr.detectChanges()
  }

  onColumnDrop(column) {
    if (column.previousIndex !== null && column.currentTab !== null && !this.tableConfig.isDragAndDropEnabled) {
      this.isColumnDragged = true;
      const { previousIndex, currentIndex } = column
      const columns = this.displayedColumns;
      let temp = columns[previousIndex];
      columns[previousIndex] = columns[currentIndex];
      columns[currentIndex] = temp;
      this.displayedColumns = [...columns]
      this.onColumnDrag.emit({ previousIndex: column.previousIndex, currentIndex: column.currentIndex })
    }
  }

  handleColumnSelect(column) {
    this.additionalColumns = this.additionalColumns?.map((additionalColumn) => {
      if (additionalColumn.columnName == column.columnName) {
        additionalColumn.checked = !additionalColumn.checked
      }
      return additionalColumn
    })
    this.cdr.detectChanges();
  }

  handleColumnDrag() {
    this.onSaveColumnDrop.emit(this.displayedColumns)
  }

  isApplyButtonDisabled() {
    const checkedColumns = this.additionalColumns?.filter((col) => col.checked);
    const uncheckedColumns = this.additionalColumns?.filter((col) => !col.checked);
    const isEveryCheckedElementPresent = checkedColumns?.every((col) => this.displayedColumns.includes(col.columnName))
    const isEveryUnCheckedElementPresent = uncheckedColumns?.every(element => !this.displayedColumns.includes(element.columnName));
    if (!isEveryCheckedElementPresent || !isEveryUnCheckedElementPresent) {
      return false;
    }
    return isEveryCheckedElementPresent && isEveryUnCheckedElementPresent
  }

  isOptionSelected(option) {
    const checkedColumns = this.additionalColumns?.filter((col) => {
      if (col.checked) {
        return col.columnName;
      }
    })
    return checkedColumns.some(col => col.columnName == option)
  }

  onApply() {
    const { checkedColumns, uncheckedColumns } = this.additionalColumns.reduce(
      (acc, column) => {
        if (column.checked) {
          acc.checkedColumns.push(column.columnName);
        } else {
          acc.uncheckedColumns.push(column.columnName);
        }
        return acc;
      },
      { checkedColumns: [], uncheckedColumns: [] }
    );
    const upDisplayedColumns = [...this.displayedColumns];
    const displayedColumns = upDisplayedColumns.pop();
    const updatedDisplayedColumns = [
      ...this.displayedColumns,
      ...checkedColumns,
      'addRow',
    ].filter((element) => !uncheckedColumns.includes(element));
    this.displayedColumns = Array.from(new Set(updatedDisplayedColumns));
    const updatedTablecolumns = [...this.tableColumns, ...checkedColumns]
    const uniqueArray = updatedTablecolumns.filter((obj, index, self) =>
      index === self.findIndex((t) => t.dataKey === obj.dataKey)
    );
    let columns = []
    uniqueArray?.forEach((column) => {
      const index = this.additionalColumns.findIndex((col) => col.dataKey === column.dataKey);
      if ((index !== -1 && this.additionalColumns[index].checked) || index === -1) {
        columns.push(column);
      }
    });
    this.tableColumns = [...columns];
    this?.onColumnSelect?.emit({ updatedDisplayedColumns: this.displayedColumns, updatedAdditionalColumns: this.additionalColumns, updatedTableColumns: this.tableColumns });
    this.isMenuOpen = false;
  }

  onPageChange(event) {
    this.limit = event.pageSize;
    this.page = event.pageIndex;
    if (this.tableConfig.paginationConfig) {
      this.tableConfig.paginationConfig.emitPageEvent(event);
    }
  }

}

